import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Download, AlertTriangle, ChevronRight } from "lucide-react";
import { AppLayout, PageHeader } from "@/components/AppLayout";
import { DataTable, Toolbar, SearchInput, Btn, Select, Modal, Field, Textarea, Input } from "@/components/Primitives";
import { StatusBadge } from "@/components/StatusBadge";
import { api, fmtNumber, calcCbm, calcWeight, formatApiErrorDetail, API, ORIGINS, COLOURS, can } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

// The three sections of the Stock on Hand tab
const SECTIONS = [
  { key: "allocated", label: "Allocated to Orders", status: "allocated" },
  { key: "available", label: "Available (Ready for Selling)", status: "available" },
  { key: "all", label: "All Stock", status: "" },
  { key: "written_off", label: "Written Off", status: "written_off" },
  { key: "breakdown", label: "On Site - Bushmans", status: "breakdown" },
];

// Black/white text depending on background luminance.
function textOn(hex) {
  const h = (hex || "#888888").replace("#", "");
  const r = parseInt(h.slice(0, 2), 16), g = parseInt(h.slice(2, 4), 16), b = parseInt(h.slice(4, 6), 16);
  if ([r, g, b].some(Number.isNaN)) return "#171717";
  return 0.299 * r + 0.587 * g + 0.114 * b < 140 ? "#ffffff" : "#171717";
}

function BreakdownMatrix({ data }) {
  if (!data) return <div className="panel p-10 text-center text-stone-400 text-sm">Loading breakdown…</div>;
  const allBands = data.bands;
  const ex = data.extras || { awaiting_processing: 0, written_off: 0 };
  return (
    <div className="panel overflow-x-auto" data-testid="breakdown-matrix">
      <table className="min-w-full text-sm border-collapse">
        <thead>
          <tr className="bg-stone-800 text-white">
            {["Butt Class Ø", "Length", "Butt Class Ø", "Butt Ø (mm)", "Code", "Colour", "Order #", "Awaiting Paint", "Available", "Allocated", "Damaged", "Total", "CBM", "Calc Wt (t)", "CBM / Pole", "Wt / Pole (kg)"].map((h, i) => (
              <th key={i} className="px-3 py-2 text-center text-xs font-bold uppercase tracking-wider border border-stone-600 whitespace-nowrap">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {allBands.map((band, bi) => {
            const bg = band.swatch || "#888888";
            const fg = textOn(bg);
            return (
              <React.Fragment key={bi}>
                {band.rows.map((r, ri) => (
                  <tr key={`${bi}-${ri}`} style={{ background: bg, color: fg }} className="font-semibold"
                      data-testid={`breakdown-row-${band.colour}-${r.avg_label}-${r.length_label}`}>
                    <td className="px-3 py-1.5 text-center border border-stone-300/40 font-mono">{r.class_label}</td>
                    <td className="px-3 py-1.5 text-center border border-stone-300/40 font-mono">{r.length_label}</td>
                    <td className="px-3 py-1.5 text-center border border-stone-300/40 font-mono">{r.class_label}</td>
                    <td className="px-3 py-1.5 text-center border border-stone-300/40 font-mono">{r.avg_label}</td>
                    <td className="px-3 py-1.5 text-center border border-stone-300/40 font-mono">{r.code}</td>
                    <td className="px-3 py-1.5 text-center border border-stone-300/40 uppercase tracking-wider">{band.colour}</td>
                    <td className="px-3 py-1.5 text-center border border-stone-300/40 font-mono text-xs">{r.order_numbers || "—"}</td>
                    <td className="px-3 py-1.5 text-right border border-stone-300/40 font-mono">{fmtNumber(r.awaiting_paint, 0)}</td>
                    <td className="px-3 py-1.5 text-right border border-stone-300/40 font-mono">{fmtNumber(r.available, 0)}</td>
                    <td className="px-3 py-1.5 text-right border border-stone-300/40 font-mono">{fmtNumber(r.allocated, 0)}</td>
                    <td className="px-3 py-1.5 text-right border border-stone-300/40 font-mono">{fmtNumber(r.damaged, 0)}</td>
                    <td className="px-3 py-1.5 text-right border border-stone-300/40 font-mono font-bold">{fmtNumber(r.qty, 0)}</td>
                    <td className="px-3 py-1.5 text-right border border-stone-300/40 font-mono">{fmtNumber(r.cbm, 2)}</td>
                    <td className="px-3 py-1.5 text-right border border-stone-300/40 font-mono">{fmtNumber(r.weight, 2)}</td>
                    <td className="px-3 py-1.5 text-right border border-stone-300/40 font-mono">{fmtNumber(r.cbm_per_pole, 3)}</td>
                    <td className="px-3 py-1.5 text-right border border-stone-300/40 font-mono">{fmtNumber(r.weight_per_pole, 1)}</td>
                  </tr>
                ))}
                <tr style={{ background: bg, color: fg }} className="font-bold border-t-2 border-white">
                  <td className="px-3 py-1.5 border border-stone-300/40" colSpan={7}>{band.total_label || band.colour} — Total</td>
                  <td className="px-3 py-1.5 text-right border border-stone-300/40 font-mono">{fmtNumber(band.total_awaiting_paint, 0)}</td>
                  <td className="px-3 py-1.5 text-right border border-stone-300/40 font-mono">{fmtNumber(band.total_available, 0)}</td>
                  <td className="px-3 py-1.5 text-right border border-stone-300/40 font-mono">{fmtNumber(band.total_allocated, 0)}</td>
                  <td className="px-3 py-1.5 text-right border border-stone-300/40 font-mono">{fmtNumber(band.total_damaged, 0)}</td>
                  <td className="px-3 py-1.5 text-right border border-stone-300/40 font-mono">{fmtNumber(band.total_qty, 0)}</td>
                  <td className="px-3 py-1.5 text-right border border-stone-300/40 font-mono">{fmtNumber(band.total_cbm, 2)}</td>
                  <td className="px-3 py-1.5 text-right border border-stone-300/40 font-mono">{fmtNumber(band.total_weight, 0)}</td>
                  <td className="px-3 py-1.5 border border-stone-300/40" colSpan={2} />
                </tr>
                <tr><td colSpan={16} className="h-2 bg-white" /></tr>
              </React.Fragment>
            );
          })}
          <tr className="bg-emerald-900 text-white font-bold" data-testid="breakdown-grand-total">
            <td className="px-3 py-2 border border-emerald-800" colSpan={7}>GRAND TOTAL (Coloured Stock)</td>
            <td className="px-3 py-2 text-right border border-emerald-800 font-mono">{fmtNumber(data.grand_total.awaiting_paint, 0)}</td>
            <td className="px-3 py-2 text-right border border-emerald-800 font-mono">{fmtNumber(data.grand_total.available, 0)}</td>
            <td className="px-3 py-2 text-right border border-emerald-800 font-mono">{fmtNumber(data.grand_total.allocated, 0)}</td>
            <td className="px-3 py-2 text-right border border-emerald-800 font-mono">{fmtNumber(data.grand_total.damaged, 0)}</td>
            <td className="px-3 py-2 text-right border border-emerald-800 font-mono">{fmtNumber(data.grand_total.qty, 0)}</td>
            <td className="px-3 py-2 text-right border border-emerald-800 font-mono">{fmtNumber(data.grand_total.cbm, 2)}</td>
            <td className="px-3 py-2 text-right border border-emerald-800 font-mono">{fmtNumber(data.grand_total.weight, 2)}</td>
          </tr>
          <tr><td colSpan={16} className="h-2 bg-white" /></tr>
          <tr className="bg-stone-100 text-stone-700 font-semibold" data-testid="breakdown-awaiting-processing">
            <td className="px-3 py-1.5 border border-stone-300" colSpan={11}>Awaiting Processing (raw, uncoloured)</td>
            <td className="px-3 py-1.5 text-right border border-stone-300 font-mono">{fmtNumber(ex.awaiting_processing, 0)}</td>
            <td className="px-3 py-1.5 border border-stone-300" colSpan={4} />
          </tr>
          <tr className="bg-stone-100 text-stone-700 font-semibold" data-testid="breakdown-written-off">
            <td className="px-3 py-1.5 border border-stone-300" colSpan={11}>Written Off</td>
            <td className="px-3 py-1.5 text-right border border-stone-300 font-mono">{fmtNumber(ex.written_off, 0)}</td>
            <td className="px-3 py-1.5 border border-stone-300" colSpan={4} />
          </tr>
          <tr className="bg-stone-800 text-white font-bold" data-testid="breakdown-overall-total">
            <td className="px-3 py-2 border border-stone-700" colSpan={11}>OVERALL TOTAL (All Statuses)</td>
            <td className="px-3 py-2 text-right border border-stone-700 font-mono">{fmtNumber(data.overall_total, 0)}</td>
            <td className="px-3 py-2 border border-stone-700" colSpan={4} />
          </tr>
          <tr className="bg-emerald-50 text-emerald-900 font-bold" data-testid="breakdown-eit-total">
            <td className="px-3 py-2 border border-stone-300" colSpan={11}>RECEIVED FROM EIT — TOTAL</td>
            <td className="px-3 py-2 text-right border border-stone-300 font-mono">{fmtNumber(ex.received_from_eit, 0)}</td>
            <td className="px-3 py-2 border border-stone-300" colSpan={4} />
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function PointedBadge({ row }) {
  if (row.pointed) {
    return <span className="text-xs uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200" data-testid={`soh-pointed-yes-${row.id}`}>Pointed ✓</span>;
  }
  if (row.requires_pointing) {
    return <span className="text-xs uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200" data-testid={`soh-pointed-pending-${row.id}`}>Needs Pointing</span>;
  }
  return <span className="text-stone-400">—</span>;
}

function BundledBadge({ row }) {
  if (row.bundled) {
    return <span className="text-xs uppercase tracking-wider px-2 py-0.5 rounded-full bg-sky-50 text-sky-800 border border-sky-200" data-testid={`soh-bundled-yes-${row.id}`}>Bundled ✓</span>;
  }
  if (row.requires_bundling) {
    return <span className="text-xs uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200" data-testid={`soh-bundled-pending-${row.id}`}>Needs Bundling</span>;
  }
  return <span className="text-stone-400">—</span>;
}

function GermanBadge({ row }) {
  if (row.german) {
    return <span className="text-xs uppercase tracking-wider px-2 py-0.5 rounded-full bg-yellow-50 text-yellow-900 border border-yellow-300 font-semibold" data-testid={`soh-german-yes-${row.id}`}>German</span>;
  }
  return <span className="text-stone-400">—</span>;
}

export default function StockOnHand() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const initialSection = SECTIONS.some((s) => s.key === searchParams.get("section"))
    ? searchParams.get("section")
    : "allocated";
  const [section, setSection] = useState(initialSection);
  const [q, setQ] = useState("");
  const [origin, setOrigin] = useState("");
  const [butt, setButt] = useState("");
  const [colour, setColour] = useState("");
  const [lengthF, setLengthF] = useState("");
  const [orderF, setOrderF] = useState("");
  const [dnF, setDnF] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [pointedF, setPointedF] = useState("");
  const [bundledF, setBundledF] = useState("");
  const [bundlePaste, setBundlePaste] = useState("");
  const [showBundlePaste, setShowBundlePaste] = useState(false);
  const [page, setPage] = useState(1);
  const [breakdown, setBreakdown] = useState(null);
  const [bdPointed, setBdPointed] = useState("pointed");
  const [asAt, setAsAt] = useState("");
  const [damageTarget, setDamageTarget] = useState(null);
  const [damageReason, setDamageReason] = useState("");
  const [damageSalvageable, setDamageSalvageable] = useState("yes");
  const [damageQty, setDamageQty] = useState("");
  const [expanded, setExpanded] = useState({});
  const [selected, setSelected] = useState(() => new Set());
  const [finishDlg, setFinishDlg] = useState(null); // {type:'pointing'|'bundling', mode:'complete'|'queue'}
  const [articleDlg, setArticleDlg] = useState(false);
  const [articleNo, setArticleNo] = useState("");
  const [finishNote, setFinishNote] = useState("");
  const [finishSize, setFinishSize] = useState("");
  const [finishKeep, setFinishKeep] = useState(false);
  const [finishBundleNo, setFinishBundleNo] = useState("");
  const canDamage = can(user?.role, "damages:create");
  const isAdmin = user?.role === "admin";
  const canFinish = can(user?.role, "processing:create"); // can select & action poles (incl. admin)
  const canEditDate = can(user?.role, "processing:update");
  const canSelect = canFinish || canEditDate;
  const [dateDlg, setDateDlg] = useState(false);
  const [newDate, setNewDate] = useState("");

  const submitDateDlg = async () => {
    if (!newDate) { toast.error("Pick a processed date"); return; }
    setDateDlg(false);
    try {
      const r = await api.post("/stock/set-processed-date", { stock_item_ids: [...selected], date_processed: newDate });
      toast.success(`Processed date set to ${r.data.date_processed} on ${r.data.updated} pole(s)`);
      setSelected(new Set());
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };
  const toggleGroup = (k) => setExpanded((e) => ({ ...e, [k]: !e[k] }));

  const toggleRow = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleMany = (rows) => setSelected((prev) => {
    const ids = rows.map((r) => r.id);
    const allSel = ids.length > 0 && ids.every((id) => prev.has(id));
    const next = new Set(prev);
    ids.forEach((id) => (allSel ? next.delete(id) : next.add(id)));
    return next;
  });

  const markPointed = async (ids, value = true) => {
    if (!ids.length) return;
    try {
      const r = await api.post("/stock/mark-pointed", { stock_item_ids: ids, pointed: value });
      toast.success(`Marked ${r.data.pointed} pole(s) ${value ? "pointed" : "not pointed"}`);
      setSelected(new Set());
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const submitArticleDlg = async () => {
    setArticleDlg(false);
    try {
      const r = await api.post("/stock/set-article", { stock_item_ids: [...selected], article_number: articleNo });
      toast.success(`Article number ${r.data.article_number ? `set to ${r.data.article_number}` : "cleared"} on ${r.data.updated} pole(s)`);
      setSelected(new Set());
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const markGerman = async (ids, value = true) => {
    if (!ids.length) return;
    try {
      const r = await api.post("/stock/mark-german", { stock_item_ids: ids, german: value });
      toast.success(`Marked ${r.data.updated} pole(s) ${value ? "GERMAN" : "not German"}`);
      setSelected(new Set());
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const markBundled = async (ids, bundleSize, keepNumbers, bundleNo) => {
    if (!ids.length) return;
    try {
      const r = await api.post("/stock/mark-bundled", {
        stock_item_ids: ids,
        bundle_size: keepNumbers || bundleNo ? null : (bundleSize || null),
        keep_numbers: !!keepNumbers,
        bundle_number: !keepNumbers && bundleNo ? bundleNo : null,
      });
      const n = (r.data.bundles || []).length;
      toast.success(keepNumbers
        ? `Marked ${r.data.bundled} pole(s) bundled — kept ${n} existing bundle number(s)`
        : bundleNo
          ? `Added ${r.data.bundled} pole(s) to bundle ${r.data.bundle_number}`
          : `Created ${n} bundle(s) from ${r.data.bundled} pole(s)${n === 1 ? ` — ${r.data.bundle_number}` : ""}`);
      setSelected(new Set());
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  // Non-admins don't finish on the spot — they create a job in Processing.
  const queueFinishing = async (ids, type, note, bundleSize) => {
    if (!ids.length) return;
    try {
      const r = await api.post("/stock/queue-finishing", { stock_item_ids: ids, type, note: note || null, bundle_size: bundleSize || null });
      toast.success(`Sent ${r.data.queued} pole(s) to ${type} — job ${r.data.job_ref} created in Processing`);
      setSelected(new Set());
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const openFinishDlg = (type, mode) => { setFinishNote(""); setFinishSize(""); setFinishKeep(false); setFinishBundleNo(""); setFinishDlg({ type, mode }); };
  const submitFinishDlg = async () => {
    const ids = [...selected];
    const { type, mode } = finishDlg;
    setFinishDlg(null);
    if (mode === "complete") {
      await markBundled(ids, finishSize ? Number(finishSize) : null, finishKeep, finishBundleNo.trim()); // admin bundling w/ size
    } else {
      await queueFinishing(ids, type, finishNote, type === "bundling" && finishSize ? Number(finishSize) : null);
    }
  };

  useEffect(() => {
    const sec = searchParams.get("section");
    if (sec && SECTIONS.some((s) => s.key === sec)) setSection(sec);
  }, [searchParams]);

  // Reset bulk selection whenever the view or filters change.
  useEffect(() => { setSelected(new Set()); }, [section, origin, butt, colour, q, asAt, lengthF, orderF, dnF, dateFrom, dateTo, pointedF, bundledF]);

  const load = async () => {
    const params = {};
    if (origin) params.origin = origin;
    if (butt) params.butt_size = butt;
    if (colour) params.colour = colour;
    if (q) params.q = q;
    if (asAt) params.as_at = asAt;
    const r = await api.get("/stock", { params });
    setItems(r.data);
  };

  useEffect(() => {
    let active = true;
    (async () => {
      const params = {};
      if (origin) params.origin = origin;
      if (butt) params.butt_size = butt;
      if (colour) params.colour = colour;
      if (q) params.q = q;
      if (asAt) params.as_at = asAt;
      const r = await api.get("/stock", { params });
      if (active) setItems(r.data);
    })();
    return () => { active = false; };
  }, [origin, butt, colour, q, asAt]);

  const doMarkDamaged = async () => {
    try {
      const r = await api.post(`/stock/${damageTarget.id}/mark-damaged`, {
        reason: damageReason || null,
        salvageable: damageSalvageable === "yes",
        quantity: damageQty ? Number(damageQty) : null,
      });
      const split = r.data?.remaining != null;
      toast.success(damageSalvageable === "yes"
        ? `Marked damaged — now in Processing → Awaiting Salvage${split ? ` (${r.data.remaining} pole(s) stay in the bundle)` : ""}`
        : "Written off — not salvageable");
      setDamageTarget(null);
      setDamageReason("");
      setDamageQty("");
      setDamageSalvageable("yes");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  // Client-side extra filters (length, order #, delivery note #, date range, pointed, bundled)
  const bundleTokens = bundlePaste.split(/[\s,;]+/).map((t) => t.trim().toUpperCase()).filter(Boolean);
  const bundleSet = new Set(bundleTokens);
  const matchesFilters = (x) => {
    if (bundleSet.size
        && !bundleSet.has((x.bundle_number || "").toUpperCase())
        && !bundleSet.has(x.id.slice(0, 8).toUpperCase())) return false;
    if (lengthF && Number(x.length) !== Number(lengthF)) return false;
    if (orderF && !(x.order_number || "").toLowerCase().includes(orderF.toLowerCase())) return false;
    if (dnF && !(x.delivery_note_number || "").toLowerCase().includes(dnF.toLowerCase())) return false;
    if (dateFrom && x.date_received && x.date_received < dateFrom) return false;
    if (dateTo && x.date_received && x.date_received > dateTo) return false;
    if (pointedF === "yes" && !x.pointed) return false;
    if (pointedF === "no" && x.pointed) return false;
    if (bundledF === "yes" && !x.bundled) return false;
    if (bundledF === "no" && x.bundled) return false;
    return true;
  };
  const filteredItems = items.filter(matchesFilters);
  const anyExtraFilter = q || origin || butt || colour || lengthF || orderF || dnF || dateFrom || dateTo || pointedF || bundledF || bundleSet.size > 0;
  const clearFilters = () => {
    setQ(""); setOrigin(""); setButt(""); setColour(""); setLengthF(""); setOrderF("");
    setDnF(""); setDateFrom(""); setDateTo(""); setPointedF(""); setBundledF(""); setBundlePaste("");
  };

  const activeSection = SECTIONS.find((s) => s.key === section) || SECTIONS[2];  const sectionItems = activeSection.status
    ? filteredItems.filter((x) => x.status === activeSection.status)
    : filteredItems;

  const total = sectionItems.reduce(
    (acc, x) => ({
      qty: acc.qty + (x.quantity || 0),
      cbm: acc.cbm + calcCbm(x.butt_size, x.length, x.quantity),
      weight: acc.weight + (x.weight || 0),
    }),
    { qty: 0, cbm: 0, weight: 0 }
  );

  const counts = {
    allocated: filteredItems.filter((x) => x.status === "allocated").length,
    available: filteredItems.filter((x) => x.status === "available").length,
    all: filteredItems.length,
    written_off: filteredItems.filter((x) => x.status === "written_off").length,
    breakdown: breakdown ? breakdown.overall_total : "—",
  };

  // Pagination for the flat table: render is paged, but ALL rows stay loaded —
  // totals, filters, paste-matching and exports always cover the full dataset.
  const PAGE_SIZE = 200;
  useEffect(() => { setPage(1); }, [section, q, origin, butt, colour, lengthF, orderF, dnF, dateFrom, dateTo, pointedF, bundledF, bundlePaste, asAt]);
  const pageCount = Math.max(1, Math.ceil(sectionItems.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const pageRows = sectionItems.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const Pager = () => pageCount > 1 && (
    <div className="flex items-center justify-end gap-3 text-xs my-2" data-testid="soh-pager">
      <span className="text-stone-500 font-mono" data-testid="soh-pager-info">
        Showing {fmtNumber((safePage - 1) * PAGE_SIZE + 1)}–{fmtNumber(Math.min(safePage * PAGE_SIZE, sectionItems.length))} of {fmtNumber(sectionItems.length)} lines — totals above cover everything
      </span>
      <Btn size="sm" variant="secondary" onClick={() => setPage(Math.max(1, safePage - 1))} disabled={safePage <= 1} testid="soh-prev-page">‹ Prev</Btn>
      <span className="font-mono">{safePage} / {pageCount}</span>
      <Btn size="sm" variant="secondary" onClick={() => setPage(Math.min(pageCount, safePage + 1))} disabled={safePage >= pageCount} testid="soh-next-page">Next ›</Btn>
    </div>
  );

  // Load the colour-coded code breakdown when that view is opened.
  useEffect(() => {
    if (section !== "breakdown") return;
    let active = true;
    (async () => {
      try {
        const params = { pointed: bdPointed === "pointed" };
        if (asAt) params.as_at = asAt;
        const r = await api.get("/stock/breakdown", { params });
        if (active) setBreakdown(r.data);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load breakdown");
      }
    })();
    return () => { active = false; };
  }, [section, asAt, bdPointed]);

  // Per-order grouping with subtotals (for the Allocated section)
  const allocatedGroups = (() => {
    const acc = {};
    for (const x of sectionItems) {
      const key = x.order_number || "— (no order #)";
      if (!acc[key]) acc[key] = { order_number: key, order_id: x.order_id, items: [], qty: 0, cbm: 0, weight: 0 };
      const g = acc[key];
      g.items.push(x);
      g.qty += x.quantity || 0;
      g.cbm += calcCbm(x.butt_size, x.length, x.quantity);
      g.weight += x.weight || 0;
    }
    return Object.values(acc).sort((a, b) => (a.order_number || "").localeCompare(b.order_number || ""));
  })();

  const dash = (v) => (v == null || v === "" ? "—" : v);

  const baseColumns = [
    { header: "Date Received", mono: true, render: (r) => dash(r.date_received) },
    { header: "Date Processed", mono: true, render: (r) => <span data-testid={`soh-date-processed-${r.id}`}>{dash(r.date_processed)}</span> },
    { header: "Mode", render: (r) => dash(r.mode) },
    { header: "Processed By", render: (r) => (
      r.processed_by_us == null ? "—" : (
        <span className={`text-[10px] px-1.5 py-0.5 rounded-sm font-bold tracking-wider ${r.processed_by_us ? "bg-teal-100 text-teal-900" : "bg-stone-100 text-stone-600"}`} data-testid={`soh-pbu-${r.id}`}>
          {r.processed_by_us ? "US" : "SUPPLIER"}
        </span>
      )
    )},
    { header: "Product Type", render: (r) => dash(r.product_type) },
    { header: "Product", render: (r) => dash(r.product_name) },
    { header: "Delivery Note #", mono: true, render: (r) => dash(r.delivery_note_number) },
    { header: "Order #", mono: true, render: (r) => dash(r.order_number) },
    { header: "Export Destination", render: (r) => dash(r.export_destination) },
    { header: "Bundle/Pole #", mono: true, render: (r) => dash(r.bundle_number) },
    { header: "Article #", mono: true, render: (r) => dash(r.article_number) },
    { header: "Weighbridge #", mono: true, render: (r) => dash(r.weighbridge_number) },
    { header: "Origin", render: (r) => dash(r.origin) },
    { header: "Transporter", render: (r) => dash(r.transporter) },
    { header: "Truck Registration", mono: true, render: (r) => dash(r.truck_registration) },
    { header: "Butt Size (mm)", mono: true, align: "right", render: (r) => dash(r.butt_size) },
    { header: "Thick (mm)", mono: true, align: "right", render: (r) => dash(r.thick) },
    { header: "Wide (mm)", mono: true, align: "right", render: (r) => dash(r.wide) },
    { header: "Length (m)", mono: true, align: "right", render: (r) => dash(r.length) },
    { header: "Quantity", mono: true, align: "right", render: (r) => fmtNumber(r.quantity) },
    { header: "Butt Colour", render: (r) => dash(r.colour) },
    { header: "Incoming Total Weight", mono: true, align: "right", render: (r) => fmtNumber(r.weight) },
    { header: "Avg Incoming Pole Weight", mono: true, align: "right", render: (r) => dash(r.avg_pole_weight) },
    { header: "CBM", mono: true, align: "right", render: (r) => fmtNumber(calcCbm(r.butt_size, r.length, r.quantity), 4) },
    { header: "Calculated Weight (kg)", mono: true, align: "right", render: (r) => fmtNumber(calcWeight(r.butt_size, r.length, r.quantity)) },
    { header: "Status", render: (r) => <StatusBadge status={r.status} /> },
    { header: "Pointed", render: (r) => <PointedBadge row={r} /> },
    { header: "German", render: (r) => <GermanBadge row={r} /> },
    { header: "Bundled", render: (r) => <BundledBadge row={r} /> },
    { header: "Damaged", render: (r) => (r.status === "damaged" ? "Yes" : "No") },
    { header: "Documents", mono: true, align: "right", render: (r) => (r.document_ids?.length || 0) },
    { header: "Comments", render: (r) => dash(r.comments) },
    { header: "Actions", align: "right", render: (r) => (
      canDamage && ["available", "allocated", "processed"].includes(r.status) ? (
        <button onClick={() => { setDamageTarget(r); setDamageReason(""); setDamageQty(""); }}
                className="p-1 text-stone-600 hover:text-amber-700" title="Mark damaged"
                data-testid={`soh-mark-damaged-${r.id}`}>
          <AlertTriangle className="h-3.5 w-3.5" />
        </button>
      ) : null
    )},
  ];

  // Show a selection column for users who can action poles (admins complete it,
  // others create a Processing job).
  const buildColumns = (rows) => {
    if (!canSelect) return baseColumns;
    const ids = rows.map((r) => r.id);
    const allSel = ids.length > 0 && ids.every((id) => selected.has(id));
    return [
      {
        header: (
          <input type="checkbox" checked={allSel} onChange={() => toggleMany(rows)}
                 title="Select all in view" data-testid="soh-select-all" />
        ),
        render: (r) => (
          <input type="checkbox" checked={selected.has(r.id)}
                 onChange={(e) => { e.stopPropagation(); toggleRow(r.id); }}
                 data-testid={`soh-select-${r.id}`} />
        ),
      },
      ...baseColumns,
    ];
  };

  return (
    <AppLayout>
      <PageHeader
        title="Live Stock on Hand"
        subtitle="Real-time bundle-level inventory, split by allocation status."
        actions={
          section === "breakdown" ? (
            <div className="flex items-center gap-2">
              <Btn variant="secondary" as="a" href={`${API}/stock/breakdown.xlsx${asAt ? `?as_at=${asAt}` : ""}`} target="_blank" testid="export-breakdown-xlsx">
                <Download className="h-4 w-4" /> Excel
              </Btn>
              <Btn variant="secondary" as="a" href={`${API}/stock/breakdown.pdf${asAt ? `?as_at=${asAt}` : ""}`} target="_blank" testid="export-breakdown-pdf">
                <Download className="h-4 w-4" /> PDF
              </Btn>
            </div>
          ) : (
            <Btn variant="secondary" as="a"
                 href={`${API}/exports/stock.xlsx${activeSection.status ? `?status=${activeSection.status}` : ""}`}
                 target="_blank" testid="export-soh">
              <Download className="h-4 w-4" /> Export {activeSection.label}
            </Btn>
          )
        }
      />

      {/* Stock as at a historical date */}
      <div className="flex flex-wrap items-center gap-3 mb-4 panel px-4 py-2.5" data-testid="soh-asat-bar">
        <label htmlFor="soh-asat-date" className="text-sm font-medium text-stone-700">Stock as at:</label>
        <input
          id="soh-asat-date" type="date" value={asAt}
          max={new Date().toISOString().slice(0, 10)}
          onChange={(e) => setAsAt(e.target.value)}
          className="px-3 py-1.5 border border-stone-300 rounded-sm text-sm font-mono focus:outline-none focus:ring-2 focus:ring-emerald-800/30 focus:border-emerald-800"
          data-testid="soh-asat-input"
        />
        {asAt ? (
          <>
            <Btn variant="secondary" as="a" href={`${API}/exports/soh-asat.xlsx?as_at=${asAt}`} target="_blank" testid="soh-asat-export">
              <Download className="h-4 w-4" /> Export as at {asAt}
            </Btn>
            <button onClick={() => setAsAt("")} className="text-xs text-stone-500 hover:text-stone-800 underline" data-testid="soh-asat-clear">
              Back to live view
            </button>
            <span className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-full px-3 py-1" data-testid="soh-asat-banner">
              Historical view — stock received on/before {asAt}; poles dispatched after this date shown as Allocated
            </span>
          </>
        ) : (
          <span className="text-xs text-stone-400">Pick a date to see (and export) the stock position on that day.</span>
        )}
      </div>

      {/* Section selector */}
      <div className="inline-flex rounded-sm border border-stone-300 overflow-hidden mb-4" data-testid="soh-sections">
        {SECTIONS.map((s) => (
          <button
            key={s.key}
            onClick={() => setSection(s.key)}
            className={`px-4 py-2 text-sm font-medium transition-colors ${section === s.key ? "bg-emerald-900 text-white" : "bg-white text-stone-700 hover:bg-stone-50"}`}
            data-testid={`soh-section-${s.key}`}
          >
            {s.label} <span className="opacity-70">({counts[s.key]})</span>
          </button>
        ))}
      </div>

      {section !== "breakdown" && (
      <Toolbar>
        <SearchInput value={q} onChange={setQ} placeholder="Search product, pole #, order..." testid="stock-search" />
        <Select value={origin} onChange={(e) => setOrigin(e.target.value)} className="max-w-[140px]" data-testid="filter-origin">
          <option value="">All origins</option>
          {ORIGINS.map((o) => <option key={o}>{o}</option>)}
        </Select>
        <input
          type="number"
          step="0.1"
          value={butt}
          onChange={(e) => setButt(e.target.value)}
          placeholder="Butt (mm)"
          className="px-3 py-2 border border-stone-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-emerald-800/30 focus:border-emerald-800 font-mono max-w-[120px]"
          data-testid="filter-butt"
        />
        <input
          type="number"
          step="0.1"
          value={lengthF}
          onChange={(e) => setLengthF(e.target.value)}
          placeholder="Length (m)"
          className="px-3 py-2 border border-stone-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-emerald-800/30 focus:border-emerald-800 font-mono max-w-[120px]"
          data-testid="filter-length"
        />
        <Select value={colour} onChange={(e) => setColour(e.target.value)} className="max-w-[140px]" data-testid="filter-colour">
          <option value="">All colours</option>
          {COLOURS.map((c) => <option key={c}>{c}</option>)}
        </Select>
        <div className="text-xs text-stone-500 ml-auto font-mono" data-testid="soh-totals">
          {sectionItems.length} bundles · {fmtNumber(total.qty)} pcs · {fmtNumber(total.cbm, 3)} CBM · {fmtNumber(total.weight)} kg
        </div>
      </Toolbar>
      )}

      {section !== "breakdown" && (
      <Toolbar>
        <input
          type="text"
          value={orderF}
          onChange={(e) => setOrderF(e.target.value)}
          placeholder="Order #"
          className="px-3 py-2 border border-stone-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-emerald-800/30 focus:border-emerald-800 font-mono max-w-[130px]"
          data-testid="filter-order-number"
        />
        <input
          type="text"
          value={dnF}
          onChange={(e) => setDnF(e.target.value)}
          placeholder="Delivery Note #"
          className="px-3 py-2 border border-stone-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-emerald-800/30 focus:border-emerald-800 font-mono max-w-[150px]"
          data-testid="filter-delivery-note"
        />
        <label className="text-xs text-stone-500">Received:</label>
        <input
          type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
          className="px-2 py-2 border border-stone-300 rounded-sm text-sm font-mono focus:outline-none focus:ring-2 focus:ring-emerald-800/30 focus:border-emerald-800"
          data-testid="filter-date-from" title="Received from"
        />
        <span className="text-xs text-stone-400">to</span>
        <input
          type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
          className="px-2 py-2 border border-stone-300 rounded-sm text-sm font-mono focus:outline-none focus:ring-2 focus:ring-emerald-800/30 focus:border-emerald-800"
          data-testid="filter-date-to" title="Received to"
        />
        <Select value={pointedF} onChange={(e) => setPointedF(e.target.value)} className="max-w-[140px]" data-testid="filter-pointed">
          <option value="">Pointed: all</option>
          <option value="yes">Pointed</option>
          <option value="no">Not pointed</option>
        </Select>
        <Select value={bundledF} onChange={(e) => setBundledF(e.target.value)} className="max-w-[140px]" data-testid="filter-bundled">
          <option value="">Bundled: all</option>
          <option value="yes">Bundled</option>
          <option value="no">Not bundled</option>
        </Select>
        <button onClick={() => setShowBundlePaste((v) => !v)}
                className={`px-3 py-2 border rounded-sm text-xs font-semibold ${bundleSet.size ? "bg-emerald-800 text-white border-emerald-800" : "border-stone-300 text-stone-600 hover:bg-stone-50"}`}
                data-testid="soh-bundle-paste-toggle">
          Paste bundle #s{bundleSet.size ? ` (${bundleSet.size})` : ""}
        </button>
        {anyExtraFilter && (
          <button onClick={clearFilters} className="text-xs text-emerald-800 hover:text-emerald-900 underline" data-testid="soh-clear-filters">
            Clear all filters
          </button>
        )}
      </Toolbar>
      )}

      {section !== "breakdown" && showBundlePaste && (
        <div className="mb-3 border border-stone-200 rounded-sm bg-white p-3 space-y-1.5" data-testid="soh-bundle-paste-panel">
          <div className="text-xs text-stone-600">
            Paste bundle numbers straight from Excel (one per line, or separated by spaces/commas) — the list below filters to only those bundles.
          </div>
          <Textarea rows={3} value={bundlePaste} onChange={(e) => setBundlePaste(e.target.value)}
                    placeholder={"BDL-0001\nBDL-0002\nBDL-0003"} className="font-mono text-xs"
                    data-testid="soh-bundle-paste-input" />
          {bundleTokens.length > 0 && (() => {
            const found = bundleTokens.filter((t) => items.some((x) => (x.bundle_number || "").toUpperCase() === t || x.id.slice(0, 8).toUpperCase() === t));
            const missing = bundleTokens.filter((t) => !found.includes(t));
            return (
              <div className="text-xs" data-testid="soh-bundle-paste-result">
                <span className="text-emerald-800 font-semibold">{found.length} of {bundleTokens.length} pasted numbers found.</span>
                {missing.length > 0 && (
                  <span className="text-amber-700"> Not found: <span className="font-mono">{missing.slice(0, 20).join(", ")}{missing.length > 20 ? "…" : ""}</span></span>
                )}
              </div>
            );
          })()}
        </div>
      )}

      {section === "written_off" && (
        <div className="mb-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-sm px-4 py-2.5" data-testid="written-off-banner">
          Written-off poles are excluded from live stock totals. An admin can restore a pole by deleting its
          record on the <span className="font-semibold">Damages</span> tab — write-offs from processing return to the
          raw receipt for re-processing; finished poles return to Available.
        </div>
      )}

      {canSelect && selected.size > 0 && (
        <div className="flex flex-wrap items-center gap-3 mb-3 panel px-4 py-2.5 bg-emerald-50/60 border-emerald-200" data-testid="soh-bulk-bar">
          <span className="text-sm font-medium text-emerald-900">{selected.size} pole(s) selected</span>
          {!isAdmin && canFinish && (
            <span className="text-xs text-stone-500">Actions create a job in Processing →</span>
          )}
          {canEditDate && (
            <Btn variant="secondary" onClick={() => { setNewDate(""); setDateDlg(true); }} testid="soh-bulk-set-date">
              Set Processed Date…
            </Btn>
          )}
          {!canFinish ? null : isAdmin ? (
            <>
              <Btn onClick={() => markPointed([...selected], true)} testid="soh-bulk-mark-pointed">
                Mark Pointed
              </Btn>
              <Btn variant="secondary" onClick={() => markPointed([...selected], false)} testid="soh-bulk-unmark-pointed">
                Not Pointed
              </Btn>
              <span className="h-5 w-px bg-stone-300" />
              <Btn onClick={() => markGerman([...selected], true)} testid="soh-bulk-mark-german">
                Mark German
              </Btn>
              <Btn variant="secondary" onClick={() => markGerman([...selected], false)} testid="soh-bulk-unmark-german">
                Not German
              </Btn>
              <span className="h-5 w-px bg-stone-300" />
              <Btn onClick={() => openFinishDlg("bundling", "complete")} testid="soh-bulk-mark-bundled">
                Mark Bundled…
              </Btn>
              <span className="h-5 w-px bg-stone-300" />
              <Btn variant="secondary" onClick={() => { setArticleNo(""); setArticleDlg(true); }} testid="soh-bulk-set-article">
                Set Article #…
              </Btn>
            </>
          ) : (
            <>
              <Btn onClick={() => openFinishDlg("pointing", "queue")} testid="soh-bulk-queue-pointing">
                Send to Pointing…
              </Btn>
              <Btn onClick={() => openFinishDlg("bundling", "queue")} testid="soh-bulk-queue-bundling">
                Send to Bundling…
              </Btn>
            </>
          )}
          <button onClick={() => setSelected(new Set())} className="text-xs text-stone-500 hover:text-stone-800 ml-auto" data-testid="soh-clear-selection">
            Clear selection
          </button>
        </div>
      )}

      {section === "breakdown" ? (
        <>
          <div className="inline-flex rounded-sm border border-stone-300 overflow-hidden mb-4" data-testid="breakdown-pointed-toggle">
            {[{ key: "pointed", label: "Pointed" }, { key: "not_pointed", label: "Not Pointed" }].map((t) => (
              <button
                key={t.key}
                onClick={() => { setBreakdown(null); setBdPointed(t.key); }}
                className={`px-4 py-2 text-sm font-medium transition-colors ${bdPointed === t.key ? "bg-stone-800 text-white" : "bg-white text-stone-700 hover:bg-stone-50"}`}
                data-testid={`breakdown-toggle-${t.key}`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <BreakdownMatrix data={breakdown} />
        </>
      ) : section === "allocated" ? (
        <div className="space-y-2" data-testid="allocated-groups">
          {allocatedGroups.length === 0 && (
            <div className="panel p-10 text-center text-stone-400 text-sm">No allocated stock.</div>
          )}
          {allocatedGroups.map((g) => (
            <div key={g.order_number} className="panel overflow-hidden" data-testid={`order-group-${g.order_number}`}>
              <button
                onClick={() => toggleGroup(g.order_number)}
                className="w-full flex items-center justify-between px-4 py-3 hover:bg-stone-50 text-left"
                data-testid={`order-group-toggle-${g.order_number}`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <ChevronRight className={`h-4 w-4 shrink-0 transition-transform ${expanded[g.order_number] ? "rotate-90" : ""}`} />
                  <span className="font-semibold truncate">Order {g.order_number}</span>
                  <span className="text-xs text-stone-500 shrink-0">{g.items.length} bundles</span>
                </div>
                <div className="flex items-center gap-4 text-xs font-mono text-stone-600 shrink-0">
                  <span>{fmtNumber(g.qty)} pcs</span>
                  <span>{fmtNumber(g.cbm, 3)} CBM</span>
                  <span>{fmtNumber(g.weight)} kg</span>
                  {g.order_id && (
                    <a
                      href={`${API}/exports/stock.xlsx?order_id=${g.order_id}`}
                      target="_blank" rel="noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="text-emerald-800 hover:underline inline-flex items-center gap-1"
                      data-testid={`order-group-export-${g.order_number}`}
                    >
                      <Download className="h-3.5 w-3.5" /> Export
                    </a>
                  )}
                </div>
              </button>
              {expanded[g.order_number] && (
                <DataTable testid={`order-table-${g.order_number}`} columns={buildColumns(g.items)} rows={g.items} />
              )}
            </div>
          ))}
        </div>
      ) : (
        <>
          <Pager />
          <DataTable testid="stock-table" columns={buildColumns(pageRows)} rows={pageRows} />
          <Pager />
        </>
      )}

      {damageTarget && (
        <Modal open onClose={() => setDamageTarget(null)} title="Mark Pole as Damaged" size="md"
               testid="soh-mark-damaged-modal"
               footer={<><Btn variant="secondary" onClick={() => setDamageTarget(null)}>Cancel</Btn>
                 <Btn variant="danger" onClick={doMarkDamaged} disabled={!damageReason.trim()} testid="soh-mark-damaged-confirm">Mark Damaged</Btn></>}>
          <div className="text-sm text-stone-700 mb-3">
            Mark <span className="font-semibold">{damageTarget.bundle_number || damageTarget.product_name || damageTarget.id.slice(0, 8)}</span> as damaged.
            {damageSalvageable === "yes"
              ? <> It will move to <span className="font-semibold">Processing → Awaiting Salvage</span> and drop out of available stock.</>
              : <> It will be <span className="font-semibold">written off</span> (not salvageable) and removed from inventory.</>}
          </div>
          <Field label="Salvageable?">
            <Select value={damageSalvageable} onChange={(e) => setDamageSalvageable(e.target.value)} data-testid="soh-mark-damaged-salvageable">
              <option value="yes">Yes — send to Awaiting Salvage</option>
              <option value="no">No — write off (not salvageable)</option>
            </Select>
          </Field>
          {(Number(damageTarget.quantity) || 1) > 1 && (
            <Field label={`Quantity to mark damaged (of ${damageTarget.quantity})`}>
              <Input type="number" min="1" max={damageTarget.quantity} value={damageQty}
                     onChange={(e) => setDamageQty(e.target.value)}
                     placeholder={`Leave blank = all ${damageTarget.quantity}`}
                     data-testid="soh-mark-damaged-qty" />
              {damageQty && Number(damageQty) < Number(damageTarget.quantity) && (
                <div className="text-[11px] text-stone-500 mt-1" data-testid="soh-mark-damaged-split-note">
                  {Number(damageTarget.quantity) - Number(damageQty)} pole(s) stay live under bundle{" "}
                  <span className="font-mono">{damageTarget.bundle_number || "—"}</span>; a replacement can be
                  bundled back under the same number later.
                </div>
              )}
            </Field>
          )}
          <Field label="Reason" required>
            <Textarea rows={2} value={damageReason} onChange={(e) => setDamageReason(e.target.value)}
                      placeholder="e.g. Split end found during inspection" data-testid="soh-mark-damaged-reason" />
          </Field>
        </Modal>
      )}

      {finishDlg && (
        <Modal open onClose={() => setFinishDlg(null)}
               title={finishDlg.mode === "complete" ? "Bundle Poles" : (finishDlg.type === "pointing" ? "Send to Pointing" : "Send to Bundling")}
               size="md" testid="soh-finish-modal"
               footer={<><Btn variant="secondary" onClick={() => setFinishDlg(null)}>Cancel</Btn>
                 <Btn onClick={submitFinishDlg} testid="soh-finish-confirm">
                   {finishDlg.mode === "complete" ? "Create Bundles" : "Create Job"}
                 </Btn></>}>
          <div className="text-sm text-stone-700 mb-3">
            {selected.size} pole(s) selected.
            {finishDlg.mode === "complete"
              ? (finishKeep ? " They will be marked bundled keeping their current bundle numbers." : " They will be bundled now and each bundle gets a unique number.")
              : <> A tracked job will be created in <span className="font-semibold">Processing → Awaiting {finishDlg.type === "pointing" ? "Pointing" : "Bundling"}</span>.</>}
          </div>
          {finishDlg.mode === "queue" && (
            <Field label="Note / Reference">
              <Textarea rows={2} value={finishNote} onChange={(e) => setFinishNote(e.target.value)}
                        placeholder="e.g. Customer ABC instruction — point both ends" data-testid="soh-finish-note" />
            </Field>
          )}
          {finishDlg.type === "bundling" && finishDlg.mode === "complete" && (
            <label className="flex items-start gap-2 mb-3 cursor-pointer" data-testid="soh-finish-keep-label">
              <input type="checkbox" className="mt-0.5" checked={finishKeep}
                     onChange={(e) => setFinishKeep(e.target.checked)} data-testid="soh-finish-keep-numbers" />
              <span className="text-sm text-stone-700">
                <span className="font-semibold">Keep existing bundle numbers</span> — for poles uploaded with their own
                bundle numbers: marks them bundled without renumbering (grouped by their current bundle #).
              </span>
            </label>
          )}
          {finishDlg.type === "bundling" && !finishKeep && (
            <>
              <Field label="Reuse existing bundle number (optional)">
                <Input value={finishBundleNo} onChange={(e) => setFinishBundleNo(e.target.value)}
                       placeholder="e.g. B-0042 — adds selected pole(s) to that bundle"
                       data-testid="soh-finish-bundle-no" />
              </Field>
              {!finishBundleNo.trim() && (
                <Field label="Bundle size (poles per bundle)">
                  <Input type="number" min="1" value={finishSize} onChange={(e) => setFinishSize(e.target.value)}
                         placeholder={`Leave blank = all ${selected.size} in one bundle`} data-testid="soh-finish-size" />
                </Field>
              )}
            </>
          )}
        </Modal>
      )}
      {articleDlg && (
        <Modal open onClose={() => setArticleDlg(false)} title="Set Article Number" size="sm" testid="soh-article-modal"
               footer={<><Btn variant="secondary" onClick={() => setArticleDlg(false)}>Cancel</Btn>
                 <Btn onClick={submitArticleDlg} testid="soh-article-confirm">Apply to {selected.size} pole(s)</Btn></>}>
          <div className="text-sm text-stone-700 mb-3">
            The article number below replaces the current one on all {selected.size} selected pole(s).
            Leave blank to clear it.
          </div>
          <Field label="Article number">
            <Input value={articleNo} onChange={(e) => setArticleNo(e.target.value)}
                   placeholder="e.g. CLR-400-20/22" data-testid="soh-article-input" />
          </Field>
        </Modal>
      )}
      {dateDlg && (
        <Modal open onClose={() => setDateDlg(false)} title="Set Processed Date" size="sm" testid="soh-date-modal"
               footer={<><Btn variant="secondary" onClick={() => setDateDlg(false)}>Cancel</Btn>
                 <Btn onClick={submitDateDlg} testid="soh-date-confirm">Apply to {selected.size} pole(s)</Btn></>}>
          <div className="text-sm text-stone-700 mb-3">
            Corrects the processed date on all {selected.size} selected pole(s). Stock exports, the Master Export
            and the Tariff Model month totals will use the new date.
          </div>
          <Field label="Processed date">
            <Input type="date" value={newDate} onChange={(e) => setNewDate(e.target.value)}
                   max={new Date().toISOString().slice(0, 10)} data-testid="soh-date-input" />
          </Field>
        </Modal>
      )}
    </AppLayout>
  );
}
